// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Auth context, route guards, and the OAuth callback page (requirements 1.1, 1.2, 1.7, 1.8).
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { AUTHOR_ROLES, type Role } from '@bda/shared';
import { consumeReturnTo, currentUser, signIn, signOut, type SessionUser } from './auth';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  /** True from Sign out until the page leaves for the Cognito logout endpoint. */
  signingOut: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Injected in tests. */
  loadUser?: () => Promise<SessionUser | null>;
  doSignOut?: () => Promise<void>;
}

export function AuthProvider({ children, loadUser = currentUser, doSignOut = signOut }: AuthProviderProps) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const refresh = useCallback(async () => {
    const u = await loadUser();
    setUser(u);
    setLoading(false);
  }, [loadUser]);
  useEffect(() => {
    let active = true;
    loadUser().then((u) => {
      if (!active) return;
      setUser(u);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [loadUser]);
  const out = useCallback(async () => {
    // Set before the user is cleared, so RequireAuth does not start a sign-in redirect that races the
    // sign-out and its redirect to the Cognito logout endpoint (requirement 1.5).
    setSigningOut(true);
    setUser(null);
    try {
      await doSignOut();
    } catch {
      // The tokens are cleared locally either way; fall back to the route guard and a fresh sign-in.
      setSigningOut(false);
    }
  }, [doSignOut]);
  return (
    <AuthContext.Provider value={{ user, loading, signingOut, refresh, signOut: out }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

export const hasRole = (user: SessionUser | null, ...roles: Role[]): boolean =>
  !!user && roles.some((r) => user.roles.includes(r));

/** May use the author features: a BlueprintAuthor or an Admin (the API enforces the same rule). */
export const canAuthor = (user: SessionUser | null): boolean => hasRole(user, ...AUTHOR_ROLES);

function Loading() {
  return (
    <Box textAlign="center" padding="xxl">
      <Spinner size="large" /> <span className="sr-only">Loading</span>
    </Box>
  );
}

export interface RequireAuthProps {
  children: ReactNode;
  startSignIn?: (returnTo: string) => Promise<void>;
}

export function RequireAuth({ children, startSignIn = signIn }: RequireAuthProps) {
  const { user, loading, signingOut } = useAuth();
  const location = useLocation();
  useEffect(() => {
    if (!loading && !user && !signingOut) void startSignIn(`${location.pathname}${location.search}`);
  }, [loading, user, signingOut, location, startSignIn]);
  if (loading || !user) return <Loading />;
  if (user.roles.length === 0) return <Navigate to="/no-access" replace />;
  return <>{children}</>;
}

export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!hasRole(user, ...roles)) return <Navigate to="/no-access" replace />;
  return <>{children}</>;
}

export interface AuthCallbackProps {
  startSignIn?: (returnTo: string) => Promise<void>;
  /** How long to wait for Amplify to finish the code exchange before offering to sign in again. */
  pollMs?: number;
  tries?: number;
}

/**
 * Amplify finishes the code exchange on load; poll the session briefly. A callback URL whose code was already
 * used (page reloaded mid-exchange, Back button, bookmarked) never yields a session, so after the polling
 * window offer a fresh sign-in instead of spinning forever (found by the task 12.1 end-to-end run).
 */
export function AuthCallback({ startSignIn = signIn, pollMs = 250, tries: maxTries = 20 }: AuthCallbackProps) {
  const { refresh, user, loading } = useAuth();
  const navigate = useNavigate();
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    let tries = 0;
    const t = window.setInterval(() => {
      tries += 1;
      void refresh();
      if (tries >= maxTries) {
        window.clearInterval(t);
        setGaveUp(true);
      }
    }, pollMs);
    return () => window.clearInterval(t);
  }, [refresh, pollMs, maxTries]);
  useEffect(() => {
    if (!loading && user) navigate(consumeReturnTo(), { replace: true });
  }, [loading, user, navigate]);
  if (gaveUp && !user) {
    return (
      // Outside the app layout, so it brings its own main landmark and page heading (axe, task 12.2).
      <main>
        <Box padding="xxl">
          <SpaceBetween size="l">
            <Header variant="h1">BDA Blueprint Studio</Header>
            <Alert
              type="error"
              header="Sign-in did not finish"
              action={<Button onClick={() => void startSignIn(consumeReturnTo())}>Sign in again</Button>}
            >
              This sign-in link has expired or was already used.
            </Alert>
          </SpaceBetween>
        </Box>
      </main>
    );
  }
  return <Loading />;
}

export function defaultHome(user: SessionUser | null): string {
  if (hasRole(user, 'BlueprintAuthor')) return '/blueprints';
  if (hasRole(user, 'BlueprintViewer')) return '/shared';
  if (hasRole(user, 'Admin')) return '/admin';
  return '/no-access';
}
